import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { Action, Dispatch } from 'redux';

import { PredefinedMilisecondsTypeEnum } from '../../lib/enums';
import lang from '../../lib/language';
import { GWMarketDataSummaryBucket } from '../../lib/models/gateway/types';
import { RootState } from '../../lib/store';
import { processSymbolSubscribesAction, processSymbolUnSubscribesAction } from '../../lib/store/common-actions';
import { getCompanyLogoAndName as getLogoAndName } from '../../lib/store/fundamental';
import { isSymbolInFavourites } from '../../lib/store/helpers';
import {
  loadChartData as storeLoadChartData,
} from '../../lib/store/market-data';
import { getFavoriteStocks } from '../../lib/store/selectors';
import SymbolStatusCache from '../../lib/store-util/SymbolStatusCache';
import { FAVOURITE_SYMBOLS_MAX_LIMIT } from '../../lib/util/constants';
import { checkIfSymbolIsHalted } from '../../lib/util/HaltSymbolHelper';

import { useIsMobile } from '../../hooks';
import ChartHeader from '../Chart/SymbolChart/ChartHeader/ChartHeader';
import FavouriteSymbolsLimitModal from '../FavouriteSymbolsLimitModal/FavouriteSymbolsLimitModal';
import FavouritesModal from '../StocksResult/componets/FavouritesModal';

import styles from './SymbolDetailsHeader.module.scss';

type OwnProps = {
  symbol: string;
  handleCreateOrderSellOrBuy: (target: string, isHeaderBtn: boolean) => void;
  isUserRestricted?: boolean;
};
type Props = OwnProps & ReduxProps;

const SymbolDetailsHeader = (props: Props) => {
  const {
    symbol,
    loadChartData,
    companyLogoAndName,
    favouriteStocks,
    individual,
    isUserRestricted,
    getCompanyLogoAndName,
    subscribeSymbols,
    unsubscribeSymbols,
    handleCreateOrderSellOrBuy,
  } = props;
  const isMobile = useIsMobile();

  const [showFavouritesLimitMessage, setShowFavouritesLimitMessage] = useState<boolean>(false);
  const [isAddedToFavourites, setIsAddedToFavourites] = useState<boolean>(false);
  const [isRemovedFromFavourites, setIsRemovedFromFavourites] = useState<boolean>(false);
  const symbolsStatusCache = SymbolStatusCache.use(symbol);

  const toggleFavoritesSubscription = useCallback(() => {
    if (isSymbolInFavourites(favouriteStocks, symbol)) {
      unsubscribeSymbols(processSymbolUnSubscribesAction({
        symbolOrSymbols: symbol,
        isRemovingFromFavorites: true,
        group: 'watchlist',
        caller: 'SymbolDetailsHeader',
        userCorrelationId: individual!.user_correlation_id,
      }));
      setIsRemovedFromFavourites(true);
    } else if (favouriteStocks.length >= FAVOURITE_SYMBOLS_MAX_LIMIT) {
      setShowFavouritesLimitMessage(true);
    } else {
      subscribeSymbols(processSymbolSubscribesAction({
        symbolOrSymbols: symbol,
        isFavorites: true,
        group: 'watchlist',
        caller: 'SymbolDetailsHeader',
        userCorrelationId: individual!.user_correlation_id,
      }));
      setIsAddedToFavourites(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [favouriteStocks, unsubscribeSymbols, subscribeSymbols]);

  const logo = useMemo(() => (companyLogoAndName[symbol]
    ? companyLogoAndName[symbol]?.logo : undefined), [companyLogoAndName, symbol]);

  const url = useMemo(() => (companyLogoAndName[symbol]
    ? companyLogoAndName[symbol]?.url : undefined), [companyLogoAndName, symbol]);

  const headerProps = {
    symbol,
    logo,
    url,
    company: companyLogoAndName[symbol]?.name,
  };

  /**
   * This useRef hook is used to avoid duplicate `loadChartData` dispatches on initial (first) render.
   * Initial value `true` is changed to `false` by the last `useEffect` - it has empty array as dependencies therefore
   * it triggers on component mount only.
   * There are two useEffect hooks that dispatch `loadChartData` on first render:
   *    - one with `symbol`
   *    - and another with `currentRange` as dependency
   * The second useEffect - for `symbol` - is checking  `isFirstRef.current` value
   * and thus skips dispatching `loadChartData` on initial render.
   * On the other hand the other `useEffect` - for `symbol` - is not using the check
   * and does dispatch `loadChartData` on first render.
   */
  const isFirstRef = useRef(true);

  useEffect(() => {
    if (!companyLogoAndName[symbol]) {
      getCompanyLogoAndName();
    }
  }, [companyLogoAndName, getCompanyLogoAndName, symbol]);

  useEffect(() => {
    if (isAddedToFavourites || isRemovedFromFavourites) {
      setTimeout(() => {
        setIsAddedToFavourites(false);
        setIsRemovedFromFavourites(false);
      }, PredefinedMilisecondsTypeEnum.eightSeconds);
    }
  }, [isAddedToFavourites, isRemovedFromFavourites]);

  useEffect(() => {
    if (symbol && !isFirstRef.current) {
      loadChartData(symbol, GWMarketDataSummaryBucket.OneDay, true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol]);

  useEffect(() => {
    isFirstRef.current = false;
  }, []);

  return (
    <>
      {individual && (
      <ChartHeader
        {...headerProps}
        handleCreateOrderSellOrBuy={handleCreateOrderSellOrBuy}
        isSymbolInFavorites={isSymbolInFavourites(favouriteStocks, symbol)}
        toggleFavoritesSubscription={toggleFavoritesSubscription}
      />
      )}
      {checkIfSymbolIsHalted(symbolsStatusCache) && !isUserRestricted
        && <div className={styles.haltedSymbolInfo}>{lang.commonHaltedSymbolInfo()}</div>}
      { (isRemovedFromFavourites || isAddedToFavourites) && (
        <FavouritesModal
          isMobile={isMobile}
          isAddedToFavourites={isAddedToFavourites}
          isRemovedFromFavourites={isRemovedFromFavourites}
          symbol={symbol}
          closeModal={() => {
            setIsAddedToFavourites(false);
            setIsRemovedFromFavourites(false);
          }}
        />
      )}
      { showFavouritesLimitMessage && (
        <FavouriteSymbolsLimitModal isMobile={isMobile} closeMessage={() => setShowFavouritesLimitMessage(false)} />
      )}
    </>
  );
};

const mapStateToProps = (state: RootState, ownProps: OwnProps) => ({
  favouriteStocks: getFavoriteStocks(state),
  companyLogoAndName: state.fundamental.companyLogoAndName,
  individual: state.crm.individualExtendedInfo,
});

const mapDispatchToProps = (dispatch: Dispatch<Action>, ownProps: OwnProps) => ({
  subscribeSymbols: (data) => dispatch(data),
  unsubscribeSymbols: (data) => dispatch(data),
  getCompanyLogoAndName: () => dispatch(getLogoAndName(ownProps.symbol)),
  loadChartData: (
    symbol: string,
    bucket: GWMarketDataSummaryBucket,
    hasRangeChanged?: boolean,
  ) => dispatch(storeLoadChartData({ symbol, bucket, hasRangeChanged })),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(SymbolDetailsHeader);
